import React, { useEffect } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box, RowsSkeletonLoader } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { earnDetailsBreadcrumbAtom } from './atoms/breadcrumbAtom'
import { EarnAssetCustomerSummary } from './components/EarnAssetCustomerSummary'
import { EarnAssetDetailsHeader } from './components/EarnAssetDetailsHeader'
import { EarnAssetHistory } from './components/EarnAssetHistory'
import { EarnAssetMerchantSummary } from './components/EarnAssetMerchantSummary'
import { EarnAssetSummaryDirect } from './components/EarnAssetSummaryDirect'
import { EarnSheetProvider, useEarnSheetContext } from './context/EarnSheetProvider'
import { useEarnAssetDetailsData } from './hooks/useEarnAssetDetailsData'
import { useEarnTracking } from './hooks/useEarnTracking'
import { EARN_SHEET_ACTION_TYPE, EarnAssetDetailsAsset } from './types'
import { EARN_ASSET_DETAILS_STATE } from './types/assetDetailsState'

export const EarnAssetDetails = () => (
  <EarnSheetProvider>
    <EarnAssetDetailsContent />
  </EarnSheetProvider>
)

const EarnAssetDetailsContent = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setEarnDetailsBreadcrumb = useSetAtom(earnDetailsBreadcrumbAtom)
  const { handleSheetOpen } = useEarnSheetContext()
  const { trackPageView } = useEarnTracking()

  const handleDeposit = (asset: EarnAssetDetailsAsset) => {
    handleSheetOpen({
      type: EARN_SHEET_ACTION_TYPE.DEPOSIT,
      header: t('earn.depositSheet.header'),
      selectedCurrency: asset.asset,
      portfolioId: asset.portfolioId,
      productName: asset.productName,
    })
  }

  const handleWithdraw = (asset: EarnAssetDetailsAsset) => {
    handleSheetOpen({
      type: EARN_SHEET_ACTION_TYPE.WITHDRAW,
      header: t('earn.withdrawSheet.header'),
      selectedCurrency: asset.asset,
      portfolioId: asset.portfolioId,
      productName: asset.productName,
    })
  }

  const assetDetailsData = useEarnAssetDetailsData({
    onDeposit: handleDeposit,
    onWithdraw: handleWithdraw,
  })
  const { isStatisticsLoading, isDirectEarning, portfolioId, assetDetailsState } = assetDetailsData
  const asset = assetDetailsState === EARN_ASSET_DETAILS_STATE.READY ? assetDetailsData.asset : ''

  useEffect(() => {
    setEarnDetailsBreadcrumb(asset)
    return () => setEarnDetailsBreadcrumb('')
  }, [asset, setEarnDetailsBreadcrumb])

  useEffect(() => {
    trackPageView('details', { portfolioId })
  }, [trackPageView, portfolioId])

  if (assetDetailsState === EARN_ASSET_DETAILS_STATE.LOADING) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (assetDetailsState === EARN_ASSET_DETAILS_STATE.NOT_FOUND) {
    navigate(PathNames.NOT_FOUND)
    return null
  }

  const {
    walletId,
    epcTakesEarn,
    earnRate,
    customerEarnRate,
    summary,
    totalEarnings,
    customerSummary,
    onDeposit,
    onWithdraw,
  } = assetDetailsData

  if (isDirectEarning) {
    return (
      <>
        <EarnAssetDetailsHeader
          asset={asset}
          onWithdrawClick={onWithdraw}
          onDepositClick={onDeposit}
        />
        <Box flexCol gap={24}>
          <EarnAssetSummaryDirect
            earnRate={earnRate}
            allocatedFunds={summary.allocatedFunds}
            myLifetimeEarnings={totalEarnings.allocationEarnings}
            unallocatedFunds={summary.unallocatedFunds}
            isStatisticsLoading={isStatisticsLoading}
          />
          <EarnAssetHistory portfolioId={portfolioId} walletId={walletId} />
        </Box>
      </>
    )
  }

  return (
    <>
      <EarnAssetDetailsHeader
        asset={asset}
        onWithdrawClick={onWithdraw}
        onDepositClick={onDeposit}
      />
      <Box flexCol gap={24}>
        <Box flexCol gap={16} align="start" className=" lg:flex-row">
          <EarnAssetMerchantSummary
            earnRate={earnRate}
            allocatedFunds={summary.allocatedFunds}
            allocationEarnings={totalEarnings.allocationEarnings}
            totalEarnings={totalEarnings.totalEarnings}
            customerEarnings={totalEarnings.customerEarnings}
          />

          <EarnAssetCustomerSummary
            customerEarnRate={customerEarnRate}
            customerEarningBalance={customerSummary.customerEarningBalance}
            customerPaidAmount={customerSummary.customerPaidAmount}
            epcTakesEarn={epcTakesEarn}
            isStatisticsLoading={isStatisticsLoading}
          />
        </Box>

        <EarnAssetHistory portfolioId={portfolioId} walletId={walletId} />
      </Box>
    </>
  )
}
